import { useRef, useState } from "react";
import api from "../lib/api";
import { Upload, X, Loader2, ImageIcon, FileText } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable Cloudinary signed-upload widget.
 *
 * Props:
 *  - folder: one of "hostro/avatars" | "hostro/properties" | "hostro/kyc" | "hostro/documents" | "hostro/blog"
 *  - multiple: bool — allow multiple files (returns array via onChange)
 *  - value: string | string[] — current URL(s) (controlled)
 *  - onChange: (urls: string | string[]) => void
 *  - accept: file accept attr (default "image/*")
 *  - resourceType: "image" | "raw" | "auto" (default "image"; use "raw" for PDFs)
 *  - testId: data-testid prefix
 *  - label: optional label
 */
export default function CloudinaryUpload({
  folder,
  multiple = false,
  value,
  onChange,
  accept = "image/*",
  resourceType = "image",
  testId = "cloudinary-upload",
  label,
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const urls = Array.isArray(value) ? value : value ? [value] : [];

  const uploadOne = async (file) => {
    const sig = await api.get("/cloudinary/signature", {
      params: { folder, resource_type: resourceType },
    });
    const fd = new FormData();
    fd.append("file", file);
    fd.append("api_key", sig.data.api_key);
    fd.append("timestamp", sig.data.timestamp);
    fd.append("signature", sig.data.signature);
    fd.append("folder", sig.data.folder);
    const res = await fetch(sig.data.upload_url, { method: "POST", body: fd });
    const json = await res.json();
    if (!res.ok || !json.secure_url) {
      throw new Error(json.error?.message || "Upload failed");
    }
    return json.secure_url;
  };

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      const list = Array.from(files);
      const uploaded = [];
      for (const f of list) {
        // Cloudinary free tier limit: 10 MB image / 100 MB raw
        const limitMB = resourceType === "raw" ? 100 : 10;
        if (f.size > limitMB * 1024 * 1024) {
          toast.error(`${f.name} too large (>${limitMB} MB)`);
          continue;
        }
        const url = await uploadOne(f);
        uploaded.push(url);
      }
      if (uploaded.length === 0) return;
      if (multiple) onChange([...(urls || []), ...uploaded]);
      else onChange(uploaded[0]);
      toast.success(`Uploaded ${uploaded.length} file${uploaded.length>1?"s":""}`);
    } catch (e) {
      toast.error(e.message || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (idx) => {
    const next = urls.filter((_, i) => i !== idx);
    onChange(multiple ? next : "");
  };

  return (
    <div data-testid={testId}>
      {label && <label className="label-eyebrow mb-1 block">{label}</label>}
      <div className="flex flex-wrap gap-2 items-start">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="brut-btn white"
          data-testid={`${testId}-btn`}
        >
          {busy ? <Loader2 size={16} className="animate-spin"/> : <Upload size={16}/>}
          {busy ? "Uploading..." : "Upload"}
        </button>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={accept}
          multiple={multiple}
          onChange={(e) => handleFiles(e.target.files)}
          data-testid={`${testId}-input`}
        />

        {urls.length === 0 && (
          <span className="text-xs text-[#4A4A4A] self-center">
            {multiple ? "No files yet" : "No file"} · max {resourceType === "raw" ? "100 MB" : "10 MB"}
          </span>
        )}

        {urls.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {urls.map((u, i) => (
              <div key={i} className="relative brut-border bg-white" data-testid={`${testId}-item-${i}`}>
                {resourceType === "image" ? (
                  <img src={u} alt="" className="w-20 h-20 object-cover"/>
                ) : (
                  <a href={u} target="_blank" rel="noopener noreferrer" className="w-20 h-20 flex flex-col items-center justify-center text-xs">
                    <FileText size={20}/><span className="mt-1">File</span>
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="absolute -top-2 -right-2 bg-[#FF90E8] brut-border w-6 h-6 flex items-center justify-center"
                  data-testid={`${testId}-remove-${i}`}
                  aria-label="remove"
                >
                  <X size={12}/>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
